import { chakra, Tooltip } from '@chakra-ui/react';
import type BigNumber from 'bignumber.js';
import React from 'react';

// values below 1 with at least this many zeros after the decimal point use subscript notation: 0.000001234 -> 0.0₅1234
const MIN_ZEROS = 4;
const SIGNIFICANT_DIGITS = 4;
// other values are rounded to this many decimals, the full value stays available in the tooltip
const MAX_DECIMALS = 6;

interface Props {
  value: BigNumber;
  className?: string;
}

const CompactNumber = ({ value, className }: Props) => {
  const full = value.toFormat();
  const abs = value.abs();

  if (!abs.isZero() && abs.lt(1)) {
    const fraction = abs.toFixed().split('.')[1] ?? '';
    const zeros = fraction.match(/^0*/)?.[0].length ?? 0;

    if (zeros >= MIN_ZEROS) {
      const digits = fraction.slice(zeros, zeros + SIGNIFICANT_DIGITS).replace(/0+$/, '');
      return (
        <Tooltip label={ full }>
          <chakra.span className={ className } whiteSpace="nowrap" aria-label={ full }>
            { value.isNegative() ? '-' : '' }0.0
            <chakra.sub fontSize="0.7em" verticalAlign="-0.3em" lineHeight={ 0 } mx="1px">{ zeros }</chakra.sub>
            { digits }
          </chakra.span>
        </Tooltip>
      );
    }
  }

  const rounded = value.dp(MAX_DECIMALS).toFormat();

  return (
    <Tooltip label={ full } isDisabled={ rounded === full }>
      <chakra.span className={ className } whiteSpace="nowrap">{ rounded }</chakra.span>
    </Tooltip>
  );
};

export default React.memo(chakra(CompactNumber));
